import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { getAttendanceGpsLogs } from "services/api/attendanceApi";
import GpsMap from "components/maps/GpsMap";

export default function AttendanceDetail() {
    const { id } = useParams();

    const [logs, setLogs] = useState([]);
    const [addresses, setAddresses] = useState({});
    const [loading, setLoading] = useState(false);

    // Load GPS Logs
    const loadData = async () => {
        try {
            setLoading(true);

            const response = await getAttendanceGpsLogs(id);

            setLogs(response?.data?.gpsLogs || []);
        } catch (error) {
            console.error("GPS Logs Load Error:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, [id]);

    // Reverse Geocoding: Coordinates -> Address
    useEffect(() => {
        if (!logs.length) return;

        let cancelled = false;

        const fetchAddresses = async () => {
            for (const log of logs) {
                const latitude = Number(log.latitude);
                const longitude = Number(log.longitude);

                if (
                    !Number.isFinite(latitude) ||
                    !Number.isFinite(longitude)
                ) {
                    continue;
                }

                const key = `${latitude},${longitude}`;

                // Skip if address already loaded
                if (addresses[key]) continue;

                try {
                    const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`;

                    const response = await fetch(url, {
                        headers: {
                            Accept: "application/json",
                        },
                    });

                    if (!response.ok) {
                        throw new Error("Unable to fetch address");
                    }

                    const data = await response.json();

                    if (cancelled) return;

                    setAddresses((previous) => ({
                        ...previous,
                        [key]:
                            data?.display_name ||
                            "Address not available",
                    }));

                    // Nominatim usage policy requires requests to be
                    // limited to approximately one request per second.
                    await new Promise((resolve) =>
                        setTimeout(resolve, 1100)
                    );
                } catch (error) {
                    console.error(
                        "Reverse Geocoding Error:",
                        error
                    );

                    if (!cancelled) {
                        setAddresses((previous) => ({
                            ...previous,
                            [key]: "Unable to load address",
                        }));
                    }
                }
            }
        };

        fetchAddresses();

        return () => {
            cancelled = true;
        };
    }, [logs]);

    // Format Time
    const formatTime = (date) => {
        if (!date) return "-";

        const parsedDate = new Date(date);

        if (Number.isNaN(parsedDate.getTime())) {
            return "-";
        }

        return parsedDate.toLocaleTimeString("en-IN", {
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: true,
        });
    };

    // Format Date
    const formatDate = (date) => {
        if (!date) return "-";

        const parsedDate = new Date(date);

        if (Number.isNaN(parsedDate.getTime())) {
            return "-";
        }

        return parsedDate.toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    };

    // Normalize GPS Event Type
    const getLogType = (type) => {
        const normalizedType = String(type || "")
            .toLowerCase()
            .replace(/[\s-]/g, "_");

        if (
            normalizedType.includes("check_in") ||
            normalizedType.includes("checked_in")
        ) {
            return {
                label: "Check-in",
                className:
                    "bg-green-100 text-green-700 border-green-200",
            };
        }

        if (
            normalizedType.includes("check_out") ||
            normalizedType.includes("checked_out")
        ) {
            return {
                label: "Check-out",
                className:
                    "bg-red-100 text-red-700 border-red-200",
            };
        }

        return {
            label: type || "GPS Activity",
            className:
                "bg-blue-100 text-blue-700 border-blue-200",
        };
    };

    // Open Google Maps
    const openGoogleMaps = (latitude, longitude) => {
        const lat = Number(latitude);
        const lng = Number(longitude);

        if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
            return;
        }

        const mapsUrl = `https://www.google.com/maps?q=${lat},${lng}`;

        window.open(
            mapsUrl,
            "_blank",
            "noopener,noreferrer"
        );
    };

    return (
        <div className="mt-5 space-y-5">
            {/* GPS Tracking Map */}
            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                <div className="mb-5 flex items-center justify-between">
                    <div>
                        <h2 className="text-xl font-bold text-gray-800">
                            GPS Tracking Map
                        </h2>

                        <p className="mt-1 text-sm text-gray-500">
                            Employee location tracking
                        </p>
                    </div>

                    <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-600">
                        {logs.length} GPS Records
                    </span>
                </div>

                <GpsMap logs={logs} />
            </div>

            {/* GPS History */}
            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                <div className="mb-5 flex items-center justify-between">
                    <div>
                        <h2 className="text-xl font-bold text-gray-800">
                            GPS History
                        </h2>

                        <p className="mt-1 text-sm text-gray-500">
                            Check-in and check-out location history
                        </p>
                    </div>

                    <span className="rounded-lg bg-gray-100 px-3 py-1.5 text-sm font-semibold text-gray-600">
                        {logs.length} Records
                    </span>
                </div>

                {loading ? (
                    <div className="py-10 text-center text-sm text-gray-500">
                        Loading GPS history...
                    </div>
                ) : logs.length === 0 ? (
                    <div className="rounded-lg border border-dashed border-gray-300 py-10 text-center">
                        <p className="font-medium text-gray-600">
                            No GPS history found
                        </p>

                        <p className="mt-1 text-sm text-gray-400">
                            Location records will appear here.
                        </p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {logs.map((log) => {
                            const latitude = Number(log.latitude);
                            const longitude = Number(log.longitude);

                            const addressKey =
                                `${latitude},${longitude}`;

                            const address =
                                addresses[addressKey];

                            const typeInfo = getLogType(
                                log.logType
                            );

                            const validCoordinates =
                                Number.isFinite(latitude) &&
                                Number.isFinite(longitude);

                            return (
                                <div
                                    key={log.id}
                                    className="rounded-xl border border-gray-200 bg-white p-4 transition hover:border-blue-200 hover:shadow-sm"
                                >
                                    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                                        {/* Left Details */}
                                        <div className="flex gap-3">
                                            <div
                                                className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${
                                                    typeInfo.label ===
                                                    "Check-in"
                                                        ? "bg-green-50 text-green-600"
                                                        : typeInfo.label ===
                                                          "Check-out"
                                                        ? "bg-red-50 text-red-600"
                                                        : "bg-blue-50 text-blue-600"
                                                }`}
                                            >
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    className="h-5 w-5"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                    stroke="currentColor"
                                                    strokeWidth={2}
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        d="M12 21s8-4.5 8-11a8 8 0 10-16 0c0 6.5 8 11 8 11z"
                                                    />

                                                    <circle
                                                        cx="12"
                                                        cy="10"
                                                        r="2.5"
                                                    />
                                                </svg>
                                            </div>

                                            <div className="min-w-0">
                                                <div className="mb-2 flex flex-wrap items-center gap-2">
                                                    <span
                                                        className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${typeInfo.className}`}
                                                    >
                                                        {typeInfo.label}
                                                    </span>

                                                    <span className="text-xs text-gray-400">
                                                        {formatDate(
                                                            log.recordedAt
                                                        )}
                                                    </span>
                                                </div>

                                                {/* Time */}
                                                <p className="mb-2 text-sm font-semibold text-gray-800">
                                                    {formatTime(
                                                        log.recordedAt
                                                    )}
                                                </p>

                                                {/* Full Address */}
                                                <div className="flex items-start gap-2">
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        className="mt-0.5 h-4 w-4 shrink-0 text-gray-400"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                        stroke="currentColor"
                                                        strokeWidth={2}
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            d="M12 21s8-4.5 8-11a8 8 0 10-16 0c0 6.5 8 11 8 11z"
                                                        />

                                                        <circle
                                                            cx="12"
                                                            cy="10"
                                                            r="2.5"
                                                        />
                                                    </svg>

                                                    <div>
                                                        <p className="text-sm font-medium text-gray-700">
                                                            {address ||
                                                                (validCoordinates
                                                                    ? "Fetching address..."
                                                                    : "Location unavailable")}
                                                        </p>

                                                        {/* Coordinates kept in secondary details */}
                                                        {validCoordinates && (
                                                            <p className="mt-1 text-xs text-gray-400">
                                                                {latitude.toFixed(
                                                                    6
                                                                )}
                                                                ,{" "}
                                                                {longitude.toFixed(
                                                                    6
                                                                )}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* View Location */}
                                        <div className="shrink-0">
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    openGoogleMaps(
                                                        log.latitude,
                                                        log.longitude
                                                    )
                                                }
                                                disabled={
                                                    !validCoordinates
                                                }
                                                className="inline-flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-600 transition hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-50"
                                            >
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    className="h-4 w-4"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                    stroke="currentColor"
                                                    strokeWidth={2}
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        d="M9 20l-5-2V5l5 2m0 13l6-2m-6 2V7m6 11l5 2V5l-5-2m0 15V3m-6 4l6-2"
                                                    />
                                                </svg>

                                                View Location
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}